import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { apiClient, unwrap } from "../../api/client";
import type { components, operations } from "../../api/generated/vision-api";
import { healthWindow, type HealthGroup, type HealthRange } from "./fleet/health";

export type FleetDevice = components["schemas"]["FleetDevice"];
export type ImportResult = components["schemas"]["ImportResult"];
export type DeviceState = components["schemas"]["DeviceState"];

export type HealthFlag = NonNullable<FleetDevice["health_flags"]>[number];
export const HEALTH_FLAGS: readonly HealthFlag[] = [
  "low_heap",
  "watchdog_rising",
  "seal_failures",
  "log_full",
  "drift",
];
export const DEVICE_STATES: readonly DeviceState[] = ["stock", "claimed", "active", "suspended", "retired"];
/** The live status filter (vision-api 0.45.0, console-overview OV-42). */
export type LiveStatus = NonNullable<NonNullable<operations["listFleet"]["parameters"]["query"]>["status"]>;
export const LIVE_STATUSES: readonly LiveStatus[] = ["online", "sleeping", "offline", "never_seen"];

/** The fleet view's filters (WEB-DBG-FLT-01), kept in the URL and sent as the listFleet query parameters. */
export type FleetFilters = {
  state?: DeviceState;
  status?: LiveStatus;
  virtual?: boolean;
  product?: string;
  function?: string;
  fw_version?: string;
  org_id?: string;
  health_flag?: HealthFlag;
  q?: string;
};

const str = (v: unknown, max = 100) => (typeof v === "string" && v.trim() ? v.trim().slice(0, max) : undefined);

/** Reads the filters from the URL's search, dropping anything the contract would refuse. */
export function parseFleetFilters(search: Record<string, unknown>): FleetFilters {
  const f: FleetFilters = {};
  if (DEVICE_STATES.includes(search.state as DeviceState)) f.state = search.state as DeviceState;
  if (LIVE_STATUSES.includes(search.status as LiveStatus)) f.status = search.status as LiveStatus;
  if (search.virtual === true || search.virtual === "true") f.virtual = true;
  if (search.virtual === false || search.virtual === "false") f.virtual = false;
  if (HEALTH_FLAGS.includes(search.health_flag as HealthFlag)) f.health_flag = search.health_flag as HealthFlag;
  const uuid = str(search.org_id);
  if (uuid && /^[0-9a-f-]{36}$/i.test(uuid)) f.org_id = uuid.toLowerCase();
  for (const k of ["product", "function", "fw_version", "q"] as const) {
    const v = str(search[k]);
    if (v) f[k] = v;
  }
  return f;
}

export const fleetQuery = (filters: FleetFilters) =>
  infiniteQueryOptions({
    queryKey: ["ops", "/v1/ops/devices", filters],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(
        apiClient("ops").GET("/v1/ops/devices", { params: { query: { limit: 500, cursor: pageParam, ...filters } } }),
      ),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

export type FleetView = components["schemas"]["FleetView"];

export const fleetViewsQuery = () =>
  queryOptions({
    queryKey: ["ops", "/v1/ops/fleet/views"],
    queryFn: () => unwrap(apiClient("ops").GET("/v1/ops/fleet/views")),
  });

export function saveFleetView(name: string, filters: FleetFilters): Promise<FleetView> {
  return unwrap(apiClient("ops").POST("/v1/ops/fleet/views", { body: { name, filters } }));
}

export async function deleteFleetView(id: string): Promise<void> {
  await unwrap(apiClient("ops").DELETE("/v1/ops/fleet/views/{view_id}", { params: { path: { view_id: id } } }));
}

export type ImportSummary = {
  batch: string;
  createdBy?: string;
  units: number;
  products: string[];
  /** The units' device ids, lower case: the unknown-devices list's Import looks for its id (DTC-43). */
  ids: Set<string>;
};

export class ImportFileError extends Error {
  readonly key: string;
  constructor(key: string) {
    super(key);
    this.name = "ImportFileError";
    this.key = key;
  }
}

/**
 * A first look at a device-import document before it is sent (VisionPlatform contracts/schemas/device-import.json):
 * enough to show what will be imported and to refuse a file that is plainly something else. The server validates
 * the whole document against the schema.
 */
export function summariseImport(text: string): { doc: Record<string, unknown>; summary: ImportSummary } {
  let doc: unknown;
  try {
    doc = JSON.parse(text);
  } catch {
    throw new ImportFileError("import.notJson");
  }
  if (typeof doc !== "object" || doc === null || Array.isArray(doc)) throw new ImportFileError("import.notImport");
  const d = doc as Record<string, unknown>;
  if (d.schema !== 1 || typeof d.batch !== "string" || !Array.isArray(d.devices)) {
    throw new ImportFileError("import.notImport");
  }
  if (d.devices.length === 0) throw new ImportFileError("import.empty");
  if (d.devices.length > 10_000) throw new ImportFileError("import.tooMany");
  const products = [
    ...new Set(
      d.devices.map((u: unknown) =>
        typeof u === "object" && u && "pid" in u ? String((u as { pid: unknown }).pid) : "?",
      ),
    ),
  ].sort();
  return {
    doc: d,
    summary: {
      batch: d.batch,
      createdBy: typeof d.created_by === "string" ? d.created_by : undefined,
      units: d.devices.length,
      products,
      ids: new Set(
        d.devices.flatMap((u: unknown) =>
          typeof u === "object" && u && typeof (u as { device_id?: unknown }).device_id === "string"
            ? [(u as { device_id: string }).device_id.toLowerCase()]
            : [],
        ),
      ),
    },
  };
}

export function importDevices(doc: Record<string, unknown>): Promise<ImportResult> {
  return unwrap(apiClient("ops").POST("/v1/ops/devices/import", { body: doc }));
}

/** Fleet-wide health per day, by product or firmware version (WEB-DBG-FLT-03); the window is fixed when fetched. */
export const fleetHealthQuery = (range: HealthRange, group: HealthGroup) =>
  queryOptions({
    queryKey: ["ops", "/v1/ops/fleet/health", range, group],
    queryFn: () =>
      unwrap(
        apiClient("ops").GET("/v1/ops/fleet/health", {
          params: { query: { ...healthWindow(range, new Date()), group_by: group } },
        }),
      ),
  });
